import * as React from 'react';

import ResizeObserver from 'resize-observer-polyfill';

import {serializeFieldNames} from 'nutshell-core';

import type {FilterObject, EntityListType} from 'nutshell-core/types';

import {EntityListHeader} from '../entity-list-header';
import {calculateInnerContentHeight} from '../layout/calculate-inner-content-height';
import {calculateMainContentWidth} from '../layout/calculate-main-content-width';

import {FullPageEntityTableWrapper} from './full-page-entity-table-wrapper';

import styles from './full-page-entity-list-container.module.css';

type Props = {
    filters: FilterObject[];
    defaultFields: string[];
    selectedPage: number;
    selectedSortField: string;
    selectedFields: string[];
    onParamChange: (arg1: any, arg2: boolean) => any;
    schema: any;
    listItems: any[];
    count: number;
    unfilteredCount: number;
    unfilteredCountThreshold: number;
    entityType: EntityListType;
    scrollContainerSelector: string;
    isFirstLoad: boolean;
    isLoading: boolean;
    hasError: boolean;
    toggleItem?: (rowId: string) => void;
    includeColumnPicker: boolean;
    onRemoveAllFilters: () => void;
    customEmptyStateComponent?: React.ReactNode;
    checkRowSelected: (entityId: string) => boolean;
    isAllSelected: boolean;
    footerHeight: number;
    selectAllItems?: () => void;
    isFilterDrawerOpen: boolean;
    shiftSelectItem?: (rowId: string) => void;
    scrollToColumnIndex?: number;
    handleRowClick?: (arg1: string) => void;
};

/*
 * Shared component that acts as a container for an entity list + pagination
 * controls that sits at the bottom of a layout and "fixes" the header to the top
 * of the page when the user has scrolled enough.
 */
export class FullPageEntityListContainer extends React.Component<Props> {
    width = 800;
    // @ts-expect-error
    resizeObserver: ResizeObserver;
    pageContentDiv: HTMLElement | null | undefined;
    horizontalScrollbar: HTMLElement | null | undefined;

    componentDidMount() {
        // @ts-expect-error
        this.pageContentDiv = document.querySelector(this.props.scrollContainerSelector);
        // @ts-expect-error
        this.horizontalScrollbar = document.querySelector(
            '.public_fixedDataTable_horizontalScrollbar'
        );

        this.resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                if (entry.target === this.pageContentDiv) {
                    const cr = entry.contentRect;
                    if (cr.width !== this.width) {
                        this.width = cr.width;
                        // @ts-expect-error
                        this.horizontalScrollbar = document.querySelector(
                            '.public_fixedDataTable_horizontalScrollbar'
                        );
                        this.forceUpdate();
                    }
                }
            }
        });
        if (this.pageContentDiv) {
            this.resizeObserver.observe(this.pageContentDiv);
        }
    }

    componentWillUnmount() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    componentDidUpdate() {
        // @ts-expect-error
        const listHeader = document.querySelector(`.${styles['fixed-list-header-container']}`);
        const calculatedWidth = calculateMainContentWidth({
            width: this.width,
            isFilterDrawerOpen: this.props.isFilterDrawerOpen,
        });

        // Update the listHeader width if the total width has changed (eg. window was resized)
        if (listHeader && listHeader.clientWidth !== calculatedWidth) {
            // @ts-expect-error
            listHeader.style.width = `${calculatedWidth}px`; // account for 16px left padding
        }
    }

    render() {
        const calculatedWidth = calculateMainContentWidth({
            width: this.width,
            isFilterDrawerOpen: this.props.isFilterDrawerOpen,
        });

        const innerContentHeight = calculateInnerContentHeight({
            pageContentHeight: this.pageContentDiv ? this.pageContentDiv.clientHeight : 0,
            headerHeight: 87,
            headerOffset: 0,
            summaryBarHeight: 0,
            footerHeight: this.props.footerHeight,
            hasNavbarOffset: false,
            hasTrialStatusBarOffset: false,
        });

        return (
            <div className={styles['full-page-entity-list-container']}>
                <EntityListHeader
                    count={this.props.count}
                    defaultFields={this.props.defaultFields}
                    schema={this.props.schema}
                    selectedPage={this.props.selectedPage}
                    selectedFields={this.props.selectedFields}
                    onPageChange={this.handlePageChange}
                    onUpdateFields={this.handleUpdateFields}
                    includeColumnPicker={this.props.includeColumnPicker}
                    entityType={this.props.entityType}
                    isLoading={this.props.isLoading}
                />
                <FullPageEntityTableWrapper
                    entityType={this.props.entityType}
                    schema={this.props.schema}
                    filters={this.props.filters}
                    unfilteredCount={this.props.unfilteredCount}
                    unfilteredCountThreshold={this.props.unfilteredCountThreshold}
                    onRemoveAllFilters={this.props.onRemoveAllFilters}
                    onParamChange={this.props.onParamChange}
                    selectedFields={this.props.selectedFields}
                    selectedPage={this.props.selectedPage}
                    selectedSortField={this.props.selectedSortField}
                    onUpdateFields={this.handleUpdateFields}
                    onSortChange={this.handleSortChange}
                    onResizeColumn={this.handleResizeColumn}
                    height={innerContentHeight}
                    width={calculatedWidth}
                    toggleItem={this.props.toggleItem}
                    isFirstLoad={this.props.isFirstLoad}
                    isLoading={this.props.isLoading}
                    hasError={this.props.hasError}
                    listItems={this.props.listItems}
                    customEmptyStateComponent={this.props.customEmptyStateComponent}
                    checkRowSelected={this.props.checkRowSelected}
                    selectAllItems={this.props.selectAllItems}
                    isAllSelected={this.props.isAllSelected}
                    shiftSelectItem={this.props.shiftSelectItem}
                    scrollToColumnIndex={this.props.scrollToColumnIndex}
                    handleRowClick={this.props.handleRowClick}
                />
            </div>
        );
    }

    handleResizeColumn = () => {
        // @ts-expect-error
        this.horizontalScrollbar = document.querySelector(
            '.public_fixedDataTable_horizontalScrollbar'
        );
        this.forceUpdate();
    };

    handlePageChange = (page: number) => {
        this.props.onParamChange({list: {page}}, true);
    };

    handleSortChange = (sort: string) => {
        const newParams = getSortParams(sort);
        this.props.onParamChange(newParams, true);
    };

    handleUpdateFields = (fields: string[]) => {
        this.props.onParamChange(
            {list: {fields: {[this.props.entityType]: serializeFieldNames(fields)}}},
            true
        );
    };

    calcMinPeopleListHeight() {
        if (!this.pageContentDiv) return 0;
        const contentHeight = this.pageContentDiv.clientHeight;

        return contentHeight;
    }
}

/**
 * Gets params to update when applying a new sort
 * @param  {string} sort      - New sort to apply
 * @return {Object}           - New params to apply
 */
function getSortParams(sort: string) {
    return {
        list: {
            sort,
            page: 1,
        },
    };
}
